/**
 * Parsed Claude Code weekly-usage banner observation.
 *
 * Claude Code renders one of two banner variants in its UI:
 *
 *   1. Percentage form (approaching limit):
 *      "You've used 87% of your weekly limit · resets Nov 28"
 *
 *   2. Saturated form (already at limit, ENG-5434):
 *      "You've hit your limit · resets May 26, 5pm (UTC)"
 *
 * The reset date in the banner carries no year; we resolve it to the
 * occurrence nearest to `now` (prev/current/next year), which keeps the
 * resolved instant stable across the reset boundary (ENG-6416). The pct is
 * 0-100 inclusive;
 * the saturated form is reported as `pct = 100`. When the banner gives
 * an explicit time-of-day (saturated form), `weekResetsAt` carries that
 * exact UTC hour; the percentage form has no time component and falls
 * back to UTC midnight.
 *
 * Patterns are written defensively because the exact phrasing has
 * shifted across CC versions and we may need to add variants without
 * also having to revisit every call-site.
 */
export interface UsageBannerObservation {
  /** 0-100 inclusive. Saturated form ('hit your limit') reports 100. */
  pct: number;
  /**
   * Reset moment inferred from the banner. UTC midnight when the
   * banner doesn't include a time-of-day; the exact UTC hour when it
   * does (saturated form, ENG-5434).
   */
  weekResetsAt: Date;
}

// Accepts: "You've used", "You’ve used", "You have used", or bare "used".
// Separator class covers ASCII "-", em/en dash, and the U+00B7 middle dot
// Claude Code uses today.
const SEP = /[\s·\-–—]+/.source;
const SUBJECT = /(?:You(?:['’]ve|\s+have)?\s+)?/.source;
// Reset date: month name + day, with optional ", <hour>(:<min>)?<am|pm>" and
// optional "(UTC)" / "UTC" marker. The trailing date string is parsed by
// `parseResetDateTime`, which tolerates both shapes.
const RESET_DATE = /[A-Za-z]{3,9}\s+\d{1,2}(?:\s*,\s*\d{1,2}(?::\d{2})?\s*(?:am|pm)(?:\s*\(?UTC\)?)?)?/
  .source;

/**
 * Known banner regex variants. The capture-groups are (pct | null, "<reset>").
 */
const BANNER_PATTERNS: readonly RegExp[] = [
  // Percentage form — pct in group 1, reset in group 2.
  new RegExp(
    `${SUBJECT}used\\s+(\\d{1,3})%\\s+of\\s+your\\s+weekly\\s+limit${SEP}resets\\s+(${RESET_DATE})`,
    'i',
  ),
  // Saturated form (ENG-5434) — no pct, reset in group 1. Wrapped to keep
  // the per-pattern shape consistent with the percentage form: the parser
  // checks group 1 for a digit string and treats a missing one as pct=100.
  new RegExp(
    `${SUBJECT}hit\\s+your\\s+limit${SEP}resets\\s+(${RESET_DATE})`,
    'i',
  ),
];

/**
 * Parse a chunk of text (typically the tail of `pane.log`) for the
 * Claude Code weekly-usage banner. Returns the **most-recent** banner in
 * the text (the match with the greatest position), or null if no banner
 * is present.
 *
 * Why most-recent and not first (ENG-6284): the banner renders only
 * intermittently in the Claude Code pane, so the host scraper now reads a
 * wide pane.log tail to reliably catch it. A wide window can contain the
 * full climb of banners over the week ("75% …", "76% …", … "100% …"); the
 * latest one is the agent's current usage, so we must return the last
 * occurrence — returning the first would report a stale, lower pct and
 * could mask an at-limit agent.
 */
export function parseUsageBanner(
  text: string,
  now: Date = new Date(),
): UsageBannerObservation | null {
  let bestIndex = -1;
  let best: UsageBannerObservation | null = null;

  for (let i = 0; i < BANNER_PATTERNS.length; i++) {
    // Clone with the global flag so we can scan every occurrence, not just
    // the first. The shared BANNER_PATTERNS regexes are stateless ('i'
    // only); a per-call global clone keeps lastIndex local to this call.
    const pattern = new RegExp(BANNER_PATTERNS[i]!.source, 'gi');
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      // Guard against a zero-width match wedging the loop.
      if (match.index === pattern.lastIndex) pattern.lastIndex++;

      // Pattern 0 is the percentage form (pct in group 1, reset in group 2);
      // pattern 1 is the saturated "hit your limit" form (reset in group 1,
      // pct implicit 100).
      let pct: number;
      let resetStr: string;
      if (i === 0) {
        pct = Number.parseInt(match[1]!, 10);
        resetStr = match[2]!;
      } else {
        pct = 100;
        resetStr = match[1]!;
      }
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) continue;

      const weekResetsAt = parseResetDateTime(resetStr, now);
      if (!weekResetsAt) continue;

      // Keep the latest-positioned valid banner across both patterns.
      if (match.index >= bestIndex) {
        bestIndex = match.index;
        best = { pct, weekResetsAt };
      }
    }
  }

  return best;
}

const MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

// Matches the optional ", H[:MM]am/pm (UTC)" tail on the reset string.
// Hour is group 1, minutes (optional) group 2, am/pm group 3.
const TIME_TAIL = /,\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)(?:\s*\(?UTC\)?)?\s*$/i;

function parseResetDateTime(humanDate: string, now: Date): Date | null {
  const trimmed = humanDate.trim();

  // Split optional time tail from the leading "Mon DD" portion.
  const timeMatch = trimmed.match(TIME_TAIL);
  const dateOnly = timeMatch ? trimmed.slice(0, timeMatch.index).trim() : trimmed;

  const parts = dateOnly.split(/\s+/);
  if (parts.length !== 2) return null;

  const month = MONTHS.indexOf(
    parts[0]!.slice(0, 3).toLowerCase() as (typeof MONTHS)[number],
  );
  if (month < 0) return null;

  const day = Number.parseInt(parts[1]!, 10);
  if (!Number.isFinite(day) || day < 1 || day > 31) return null;

  let hour = 0;
  let minute = 0;
  if (timeMatch) {
    const rawHour = Number.parseInt(timeMatch[1]!, 10);
    if (!Number.isFinite(rawHour) || rawHour < 1 || rawHour > 12) return null;
    if (timeMatch[2]) {
      minute = Number.parseInt(timeMatch[2], 10);
      if (!Number.isFinite(minute) || minute < 0 || minute > 59) return null;
    }
    const isPm = timeMatch[3]!.toLowerCase() === 'pm';
    // 12am → 00:xx, 12pm → 12:xx, 1pm → 13:xx, etc.
    hour = rawHour % 12 + (isPm ? 12 : 0);
  }

  // Banner reset dates carry no year, so we must infer it. A Claude Code
  // weekly reset is always within a few days of `now` in EITHER direction:
  // right after a reset elapses the pane can still hold the just-passed
  // banner (a recent PAST date) before it re-renders the next window (a near
  // FUTURE date). The reset must therefore resolve to the SAME instant
  // whether the banner is read just before or just after the boundary
  // (ENG-6416) — a year that flips with read time pins downstream alerts
  // open for ~12 months (the ENG-6379 / ENG-6415 symptom).
  //
  // Earlier heuristics used "this year unless the candidate is more than N
  // days in the past, then roll +1" (N=1, later widened to 6 on ENG-6379).
  // Any fixed past-window still has a hard discontinuity at `now - N`: the
  // same banner text flips 2026↔2027 the moment `now` crosses it.
  //
  // Instead, resolve to the occurrence of (month, day, time) NEAREST to
  // `now` among the previous / current / next UTC year. Annual occurrences
  // are ~365 days apart, so for any realistic ±7-day banner exactly one
  // candidate is close and "nearest" is unambiguous and deterministic. The
  // only read time at which the chosen year flips is the ~6-month antipode of
  // the reset date — a point at which no weekly banner for that date is ever
  // rendered — so the boundary oscillation is eliminated for every real
  // input. This also covers the Dec↔Jan wrap in both directions for free
  // (nearest picks the adjacent year automatically).
  const baseYear = now.getUTCFullYear();
  let resolved: Date | null = null;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const y of [baseYear - 1, baseYear, baseYear + 1]) {
    const candidate = new Date(Date.UTC(y, month, day, hour, minute));
    const delta = Math.abs(candidate.getTime() - now.getTime());
    if (delta < bestDelta) {
      bestDelta = delta;
      resolved = candidate;
    }
  }
  return resolved;
}
